import { sortBy } from 'lodash';
import { useMemo } from 'react';

import { getMsUntilNextSecond, REGION_SORT_ORDER } from '#shared';

import type { HubReportStatus } from '#entities/schemas';

import { usePollingQuery, useAggregateRefetch } from '#shared/api';
import type { PollingQueryResult } from '#shared/api';

import { useGetHubsResourceReportQuery } from '../hubsResourceReport.api';

const TARGET_SECOND = 5;

const getHubsResourceReportDelay = () => getMsUntilNextSecond(TARGET_SECOND);

export const useQueryHubsResourceReport = (): PollingQueryResult<
  HubReportStatus[]
> => {
  const queryResult = useGetHubsResourceReportQuery(undefined, {
    pollingInterval: 0,
  });

  const { getDelay, refetchSignal } = useAggregateRefetch(
    'hubsResourceReport',
    getHubsResourceReportDelay,
  );
  const {
    data,
    isLoading,
    isFetching,
    isError,
    error,
    lastSentTime,
    lastReceiveTime,
  } = usePollingQuery(queryResult, {
    getDelay,
    refetchSignal,
  });

  return useMemo(() => {
    const hubsList = data?.data ?? [];
    const sortedHubs = sortBy(hubsList, hub => {
      const idx = (REGION_SORT_ORDER as readonly string[]).indexOf(
        hub.location.region,
      );
      return idx === -1 ? -1 : idx;
    });
    return {
      data: sortedHubs,
      generatedAt: data?.generatedAt ?? null,
      validation: null, // TODO: add validation when schema validation is added
      isLoading,
      isFetching,
      isError,
      error,
      lastSentTime,
      lastReceiveTime,
    };
  }, [
    data,
    isLoading,
    isFetching,
    isError,
    error,
    lastSentTime,
    lastReceiveTime,
  ]);
};
